import type { Table } from '@tanstack/react-table';
import type { DynamicTableColumnConfig } from './dynamic-table-types';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { getDisplayLabel, renderBadge, type ColumnMeta } from './DynamicTableValueRenderers';

export interface DynamicTableCardViewProps<T extends object> {
  table: Table<T>;
  columns: DynamicTableColumnConfig<T>[];
  isLoading: boolean;
  loadingMessage: string;
  emptyMessage: string;
  onRowClick?: (row: T, index: number) => void;
}

export function DynamicTableCardView<T extends object>({
  table,
  columns,
  isLoading,
  loadingMessage,
  emptyMessage,
  onRowClick,
}: DynamicTableCardViewProps<T>) {
  const data = table.getCoreRowModel().rows.map(r => r.original);
  const getRowId = (row: T) =>
    table.options.getRowId
      ? table.options.getRowId(row, 0, undefined)
      : String((row as { id?: string }).id ?? '');

  return (
    <div className="p-4">
      {isLoading ? (
        <div className="flex justify-center items-center gap-2 py-12 text-muted-foreground">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="animate-spin"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          {loadingMessage}
        </div>
      ) : data.length === 0 ? (
        <EmptyStatePanel message={emptyMessage} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {data.map((row, index) => (
            <div
              key={getRowId(row)}
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={() => onRowClick?.(row, index)}
              onKeyDown={e => onRowClick && e.key === 'Enter' && onRowClick(row, index)}
              className={`rounded-lg border border-border bg-card p-4 shadow-sm transition-colors ${onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''}`}
            >
              <div className="space-y-2">
                {columns
                  .filter(c => c.id !== 'actions')
                  .map(col => {
                    const accessorKey = (col.accessorKey ?? col.id) as string;
                    const value = (row as Record<string, unknown>)[accessorKey];
                    return (
                      <div key={col.id} className="flex flex-col gap-0.5">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {typeof col.header === 'string' ? col.header : col.id}
                        </span>
                        <div className="text-sm text-foreground">
                          {col.cell
                            ? col.cell({ row, value, index })
                            : col.columnType === 'badge'
                              ? renderBadge(
                                  value,
                                  (col.meta as ColumnMeta | undefined) ?? undefined
                                )
                              : (getDisplayLabel(
                                  value,
                                  (col.meta as ColumnMeta | undefined) ?? undefined
                                ) ?? (value == null ? '—' : String(value)))}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
